import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@shared/components/Table';
import ActionButtons from '@shared/components/ActionButtons';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import { identificatorsService } from '@modules/additionalSettings/nomenclatures/services/identificators';
import NomenclatureTranslationModal from '@modules/additionalSettings/nomenclatureTranslations/components/NomenclatureTranslationModal';
import { notifyError, notifySuccess } from '@config/tostifyConfig/tostifyConfig';

export default function NomenclatureTranslations() {
    const { t, i18n } = useTranslation();
    
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [sortBy, setSortBy] = useState(null);
    const [sortDir, setSortDir] = useState('asc');
    
    const [searchQuery, setSearchQuery] = useState({});
    const [searchType, setSearchType] = useState('code');
    
    const [selectedNomenclature, setSelectedNomenclature] = useState('');
    const [nomenclaturesOptions, setNomenclaturesOptions] = useState([]);
    const [nomenclaturesLoading, setNomenclaturesLoading] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    // Fetch nomenclatures options (determs) for the select dropdown
    useEffect(() => {
        const fetchNomenclatures = async () => {
            try {
                setNomenclaturesLoading(true);
                const response = await identificatorsService.getDeterms({
                    searchQuery: '',
                    pageNumber: 0,
                    pageSize: 1000, // Fetch all for dropdown
                    sortDir: 'asc'
                });
                setNomenclaturesOptions(response.content || []);
            } catch (error) {
                console.error('Error fetching nomenclatures:', error);
            } finally {
                setNomenclaturesLoading(false);
            }
        };
        fetchNomenclatures();
    }, []);

    useEffect(() => {
        setSelectedRecord(null);
    }, [selectedNomenclature]);

    // Fetch translations data
    const fetchData = async (params = {}) => {
        if (!selectedNomenclature) {
            setData([]);
            setTotalPages(0);
            setTotalElements(0);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            
            const searchParams = {
                searchQuery: '',
                pageNumber: params.pageNumber !== undefined ? params.pageNumber : pageNumber,
                pageSize: params.pageSize !== undefined ? params.pageSize : pageSize,
                sortDir: params.sortDir || sortDir,
                ...(searchQuery.value && { value: searchQuery.value }),
                ...(searchQuery.transValue && { transValue: searchQuery.transValue }),
                ...(searchQuery.note && { note: searchQuery.note }),
                ...(searchQuery.transNote && { transNote: searchQuery.transNote })
            };

            const response = await identificatorsService.getNomenclatureTranslations(selectedNomenclature, searchParams);
            
            setData(response.content || []);
            setTotalPages(response.totalPages || 0);
            setTotalElements(response.totalElements || 0);
            setPageNumber(response.pageNumber || 0);
            setPageSize(response.pageSize || 10);
        } catch (error) {
            console.error('Error fetching nomenclature translations:', error);
            setError(error?.response?.data?.message || t('common.errorMessage'));
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, pageSize, sortDir, selectedNomenclature]);

    const handleSearchClick = () => {
        setPageNumber(0);
        fetchData({ pageNumber: 0 });
    };

    const handleSort = (columnKey) => {
        const newSortDir = sortBy === columnKey && sortDir === 'asc' ? 'desc' : 'asc';
        setSortBy(columnKey);
        setSortDir(newSortDir);
        setPageNumber(0);
        fetchData({ pageNumber: 0, sortDir: newSortDir });
    };

    const handleRowSelect = (index) => {
        if (!data || !Array.isArray(data)) return;
        setSelectedRecord(data[index]);
    };

    const handleOpenAddModal = () => {
        if (!selectedRecord) {
            notifyError(t('common.selectRecord') || 'Please select a record first');
            return;
        }
        setIsAddModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsAddModalOpen(false);
    };

    const handleCreateTranslation = async ({ title, translationTitle, translationCode }) => {
        if (!selectedRecord || !selectedNomenclature) {
            notifyError(t('common.selectRecord') || 'Please select a record first');
            return;
        }

        const languageMap = {
            bg: 'bg_BG',
            en: 'en_GB'
        };

        const langCode = languageMap[i18n.language] || i18n.language || 'en_GB';
        const selectedDeterm = nomenclaturesOptions.find(option => String(option.id) === String(selectedNomenclature));

        const payload = {
            mnemonic: selectedRecord.mnemonic || selectedDeterm?.mnemonic || '',
            value: selectedRecord.value,
            note: title,
            transValue: translationCode,
            transNote: translationTitle,
            nomenclatureId: selectedRecord.nomenclatureId || selectedRecord.id,
            langCode
        };

        try {
            await identificatorsService.createNomenclatureTranslation(payload);
            notifySuccess(t('common.updatedSuccessfully') || 'Saved successfully');
            await fetchData();
            setIsAddModalOpen(false);
        } catch (err) {
            console.error('Error creating translation:', err);
            notifyError(err?.response?.data?.message || t('common.errorMessage'));
        }
    };

    const searchOptions = [
        { key: 'value', label: 'code' },
        { key: 'transValue', label: 'additionalCode' },
        { key: 'note', label: 'title' },
        { key: 'transNote', label: 'transNote' }
    ];

    const columns = [
        { key: 'value', label: t('additionalSettings.nomenclatureTranslations.table.headers.code') || 'Code', sortable: true },
        { key: 'transValue', label: t('additionalSettings.nomenclatureTranslations.table.headers.codeOfTranslation') || 'Code of Translation', sortable: true },
        { key: 'note', label: t('additionalSettings.nomenclatureTranslations.table.headers.title') || 'Title', sortable: true },
        { key: 'transNote', label: t('additionalSettings.nomenclatureTranslations.table.headers.translatedTitle') || 'Translated Title', sortable: true }
    ];

    const actionButtons = (
        <ActionButtons
            buttons={[
                {
                    ...COMMON_BUTTONS.ADD,
                    showWhen: 'whenSelected',
                    onClick: handleOpenAddModal
                }
            ]}
            selectedRecord={selectedRecord}
        />
    );

    const selectedRowIdentifier = selectedRecord?.id || selectedRecord?.nomenclatureId || selectedRecord?.value || null;

    return (
        <>
        <Table
            title={t('additionalSettings.nomenclatureTranslations.title')}
            columns={columns}
            data={data}
            loading={loading}
            error={error}
            actionButtons={actionButtons}
            pageNumber={pageNumber}
            pageSize={pageSize}
            totalPages={totalPages}
            totalElements={totalElements}
            sortBy={sortBy}
            sortDir={sortDir}
            searchQuery={searchQuery}
            searchType={searchType}
            searchOptions={searchOptions}
            handleSort={handleSort}
            handleCheckboxChange={handleRowSelect}
            handleSearchClick={handleSearchClick}
            setSearchQuery={setSearchQuery}
            setSearchType={setSearchType}
            setPageSize={setPageSize}
            setpageNumber={setPageNumber}
            getData={fetchData}
            showNomenclaturesSelect={true}
            selectedNomenclature={selectedNomenclature}
            setSelectedNomenclature={setSelectedNomenclature}
            nomenclaturesOptions={nomenclaturesOptions.map(determ => ({
                id: determ.id,
                nomenclature: { id: determ.id },
                name: determ.note || determ.mnemonic
            }))}
            selectedData={selectedRowIdentifier}
            showCheckbox={false}
            showPagination={true}
            showPageSizeSelector={false}
        />
        {isAddModalOpen && selectedRecord && (
            <NomenclatureTranslationModal
                onClose={handleCloseModal}
                onConfirm={handleCreateTranslation}
                record={selectedRecord}
            />
        )}
        </>
    );
}

